import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, User, ChevronDown, LogOut, Settings } from 'lucide-react';
import Dropdown from './ui/Dropdown';
import Badge from './ui/Badge';
import Avatar from './ui/Avatar';
import AuthContext from '../context/AuthContext';

const HamburgerIcon = ({ open, ...props }) => (
  <motion.div
    className="space-y-1.5"
    animate={open ? 'open' : 'closed'}
    {...props}
  >
    <motion.span
      className="block h-0.5 w-6 bg-current"
      variants={{
        closed: { rotate: 0, y: 0 },
        open: { rotate: 45, y: 6 },
      }}
    />
    <motion.span
      className="block h-0.5 w-6 bg-current"
      variants={{
        closed: { opacity: 1 },
        open: { opacity: 0 },
      }}
    />
    <motion.span
      className="block h-0.5 w-6 bg-current"
      variants={{
        closed: { rotate: 0, y: 0 },
        open: { rotate: -45, y: -6 },
      }}
    />
  </motion.div>
);

const UniversalNavbar = () => {
  const { pathname } = useLocation();
  const { user, logoutUser } = useContext(AuthContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [notificationCount] = useState(3); // Mock notification count

  // Check if user is authenticated (on protected pages)
  const isAuthenticated = user !== null && !['/Login', '/Register', '/'].includes(pathname);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Handle scroll effect for navbar background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigation links based on current page
  const getNavigationLinks = () => {
    const activeLinkClass = "bg-[var(--color-accent-light)] text-[var(--color-primary)]";
    const linkBaseClass = "text-[var(--color-gray-700)] font-medium hover:text-[var(--color-primary)] transition-all duration-300 py-2 px-4 rounded-[var(--radius-md)] hover:bg-[var(--color-gray-100)]";
    const buttonClass = "px-6 py-2.5 bg-[var(--color-primary)] text-white rounded-[var(--radius-md)] font-semibold hover:bg-[var(--color-primary-light)] transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5";

    if (pathname === '/') {
      return (
        <>
          {/* <a
            href="#benefits"
            className={linkBaseClass}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Benefits
          </a>
          <a
            href="#process"
            className={linkBaseClass}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            How It Works
          </a>
          <a
            href="#features"
            className={linkBaseClass}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Features
          </a> */}
          <Link
            to="/Login"
            className={buttonClass}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Login
          </Link>
        </>
      );
    } else if (pathname === '/Login') {
      return (
        <>
          <Link
            to="/"
            className={linkBaseClass}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Home
          </Link>
          <Link
            to="/Register"
            className={buttonClass}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Sign Up
          </Link>
        </>
      );
    } else if (pathname === '/Register') {
      return (
        <>
          <Link
            to="/"
            className={linkBaseClass}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Home
          </Link>
          <Link
            to="/Login"
            className={buttonClass}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Login
          </Link>
        </>
      );
    } else {
      return (
        <Link
          to="/Register"
          className={buttonClass}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          Get Started
        </Link>
      );
    }
  };

  // User dropdown menu items
  const getUserDropdownItems = () => {
    if (!isAuthenticated) return [];

    return [
      {
        label: 'Profile',
        icon: <User size={16} />,
        onClick: () => {
          window.location.href = '/Profile';
        },
      },
      {
        label: 'Settings',
        icon: <Settings size={16} />,
        onClick: () => {
          window.location.href = '/manage_subscription';
        },
      },
      { divider: true },
      {
        label: 'Logout',
        icon: <LogOut size={16} />,
        onClick: () => {
          if (logoutUser) logoutUser();
        },
      },
    ];
  };

  const navVariants = {
    hidden: { y: -100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 120, damping: 20, delay: 0.1 },
    },
  };

  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: { duration: 0.3, when: "afterChildren" },
    },
    open: {
      opacity: 1,
      height: "auto",
      transition: { duration: 0.3, when: "beforeChildren", staggerChildren: 0.05 },
    },
  };

  const mobileLinkVariants = {
    closed: { opacity: 0, y: -10 },
    open: { opacity: 1, y: 0 },
  };

  return (
    <motion.nav
      className={`fixed w-full top-0 transition-all duration-300 font-fell ${
        isScrolled || isMobileMenuOpen
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-[var(--color-gray-200)]'
          : 'bg-white/80 backdrop-blur-sm'
      }`}
      style={{ zIndex: 'var(--z-sticky)' }}
      variants={navVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 z-20">
            <img
              src={'https://d3qvi06vol9hrb.cloudfront.net/static/media/logo-img.e08e272c66f1e35ac87c.png'} 
              alt="VasstrInfo"
              className="h-8 md:h-10 w-auto object-contain transition-transform duration-300 hover:scale-105"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-2">
            {getNavigationLinks()}

            {/* Notification Bell - Only show when authenticated */}
            {isAuthenticated && (
              <motion.button
                className="relative p-2.5 rounded-[var(--radius-md)] text-[var(--color-gray-600)] hover:bg-[var(--color-gray-100)] hover:text-[var(--color-primary)] transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Notifications"
              >
                <Bell size={20} />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1">
                    <Badge variant="error" size="sm">
                      {notificationCount}
                    </Badge>
                  </span>
                )}
              </motion.button>
            )}

            {/* User Profile Dropdown - Only show when authenticated */}
            {isAuthenticated && (
              <Dropdown
                trigger={
                  <button className="flex items-center space-x-2 px-3 py-2 rounded-[var(--radius-md)] hover:bg-[var(--color-gray-100)] transition-colors duration-300">
                    <Avatar
                      src={user?.profile_picture}
                      alt={user?.username || 'User'}
                      fallback={user?.username?.[0] || 'U'}
                      size="sm"
                    />
                    <span className="hidden lg:block text-sm font-medium text-[var(--color-gray-700)]">
                      {user?.username || 'User'}
                    </span>
                    <ChevronDown size={16} className="text-[var(--color-gray-500)]" />
                  </button>
                }
                items={getUserDropdownItems()}
                align="right"
              />
            )}
          </div>

          {/* Mobile menu button */}
          <div
            className="md:hidden flex items-center space-x-2"
            style={{ zIndex: 'var(--z-sidebar-toggle)' }}
          >
            {/* Mobile Notification Bell - Only show when authenticated */}
            {isAuthenticated && (
              <button
                className="relative p-2 rounded-[var(--radius-md)] text-[var(--color-gray-600)] hover:bg-[var(--color-gray-100)]"
                aria-label="Notifications"
              >
                <Bell size={20} />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1">
                    <Badge variant="error" size="sm">
                      {notificationCount}
                    </Badge>
                  </span>
                )}
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              className={`p-2 rounded-[var(--radius-md)] transition-colors duration-300 touch-manipulation ${
                isMobileMenuOpen ? 'text-[var(--color-primary)]' : 'text-[var(--color-gray-700)]'
              } hover:bg-[var(--color-gray-100)] active:bg-[var(--color-gray-200)]`}
              style={{
                minHeight: '44px',
                minWidth: '44px',
                pointerEvents: 'auto'
              }}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <HamburgerIcon open={isMobileMenuOpen} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="md:hidden border-t border-[var(--color-gray-200)] bg-white/95 backdrop-blur-md overflow-hidden"
              style={{ zIndex: 'var(--z-mobile-menu)' }}
              variants={mobileMenuVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              <div className="px-4 py-6 space-y-3">
                {React.Children.map(getNavigationLinks(), (child, index) => (
                  child && React.isValidElement(child) ? (
                    <motion.div
                      key={index}
                      variants={mobileLinkVariants}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {React.cloneElement(child, {
                        className: `${child.props.className} block w-full text-center py-3 text-base touch-manipulation`,
                        style: {
                          minHeight: '44px',
                          pointerEvents: 'auto'
                        },
                        onClick: (e) => {
                          e.stopPropagation();
                          if (child.props.onClick) {
                            child.props.onClick(e);
                          }
                          setIsMobileMenuOpen(false);
                        }
                      })}
                    </motion.div>
                  ) : null
                ))}

                {/* Mobile User Menu - Only show when authenticated */}
                {isAuthenticated && (
                  <motion.div variants={mobileLinkVariants} className="pt-4 border-t border-[var(--color-gray-200)]">
                    <div className="flex items-center space-x-3 px-4 py-3 mb-3">
                      <Avatar
                        src={user?.profile_picture}
                        alt={user?.username || 'User'}
                        fallback={user?.username?.[0] || 'U'}
                        size="md"
                      />
                      <div>
                        <p className="text-sm font-semibold text-[var(--color-gray-900)]">
                          {user?.username || 'User'}
                        </p>
                        <p className="text-xs text-[var(--color-gray-500)]">
                          {user?.email || ''}
                        </p>
                      </div>
                    </div>
                    <Link
                      to="/Profile"
                      className="flex items-center space-x-3 px-4 py-3 text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)] rounded-[var(--radius-md)] transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <User size={18} />
                      <span className="text-sm font-medium">Profile</span>
                    </Link>
                    <Link
                      to="/manage_subscription"
                      className="flex items-center space-x-3 px-4 py-3 text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)] rounded-[var(--radius-md)] transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Settings size={18} />
                      <span className="text-sm font-medium">Settings</span>
                    </Link>
                    <button
                      onClick={() => {
                        if (logoutUser) logoutUser();
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center space-x-3 w-full px-4 py-3 text-[var(--color-error)] hover:bg-[var(--color-error-light)] rounded-[var(--radius-md)] transition-colors"
                    >
                      <LogOut size={18} />
                      <span className="text-sm font-medium">Logout</span>
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default UniversalNavbar;